import { useTagDictionary } from "@/hooks/useTagDictionary";
import { TagDictionary } from "@bcasmart/doxecure-commons/doxecureTypes";
import { Box, List, ListItem, Popover, PopoverBody, PopoverContent, PopoverTrigger as ChakraPopoverTrigger, Spinner, Tag, TagCloseButton, TagLabel, Text, Wrap } from "@chakra-ui/react";
import { KeyboardEvent, ReactElement, useMemo, useRef, useState } from "react";

import { $ok } from "foundation-ts/commons";

import { G1Input } from "@components/G1Input";

interface TagSelectorProps {
    selectedTagKeys: string[];
    onChange: (keys: string[]) => void;
}

export const TagSelector = (props: TagSelectorProps) => {
    const { selectedTagKeys, onChange } = props;
    const { tags, isCreating, createTag } = useTagDictionary();
    const [inputValue, setInputValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectedKeys = useMemo(() => new Set(selectedTagKeys), [selectedTagKeys]);
    const allTags = useMemo(() => Object.entries(tags) as [string, TagDictionary[string]][],[tags]);
    const matchingEntries = useMemo(() => {
        const query = inputValue.trim().toLowerCase();
        return allTags
            .filter(([key]) => !selectedKeys.has(key))
            .filter(([, tag]) => query.length === 0 || tag.name.toLowerCase().includes(query));
    }, [allTags, selectedKeys, inputValue]);

    const exactMatchExists = allTags.some(([_, t]) => t.name.toLowerCase() === inputValue.trim().toLowerCase());
    const canCreate = inputValue.trim().length > 0 && !exactMatchExists;

    const closeAndReset = () => {
        setIsOpen(false);
        setInputValue("");
    };

    const handleSelectKey = (key: string) => {
        onChange([...selectedTagKeys, key]);
        closeAndReset();
        inputRef.current?.focus();
    };

    const handleRemoveKey = (key: string) => {
        onChange(selectedTagKeys.filter(k => k !== key));
    };

    const handleCreateTag = async () => {
        const name = inputValue.trim();
        if (name.length === 0) { return; }

        const result = await createTag(name);
        if (!$ok(result)) { return; }
        onChange([...selectedTagKeys, result!.key]);
        closeAndReset();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (matchingEntries.length > 0) {
                handleSelectKey(matchingEntries[0][0]);
            } else if (canCreate) {
                handleCreateTag();
            }
        } else if (e.key === "Backspace" && inputValue.length === 0 && selectedTagKeys.length > 0) {
            handleRemoveKey(selectedTagKeys[selectedTagKeys.length - 1]);
        } else if (e.key === "Escape") {
            closeAndReset();
        }
    };

    return (
        <Box>
            {selectedTagKeys.length > 0 && (
                <Wrap spacing={2} mb={2}>
                    {selectedTagKeys.map(k => {
                        const tag = tags[k];
                        if (!tag) { return null; }
                       
                       return (
                            <Tag key={k} backgroundColor="transparent" borderWidth="1px" borderColor={tag.color} color={tag.color} borderRadius="xl" height="2.5rem">
                                <TagLabel>{tag.name}</TagLabel>
                                <TagCloseButton onClick={() => handleRemoveKey(k)} />
                            </Tag>
                       );
                    })}
                </Wrap>
            )}
            <Popover isOpen={isOpen} autoFocus={false} placement="bottom-start" matchWidth onClose={closeAndReset}>
                <PopoverTrigger>
                    <G1Input ref={inputRef} component="form" value={inputValue} placeholder="Rechercher ou créer une catégorie..."
                             onChange={(e) => { setInputValue(e.target.value); setIsOpen(true); } }
                             onFocus={() => setIsOpen(true)}
                             onKeyDown={handleKeyDown}
                    />
                </PopoverTrigger>
                <PopoverContent width="100%">
                    <PopoverBody p={1} maxH="200px" overflowY="auto">
                        <List spacing={0}>
                            {matchingEntries.map(([key, tag]) => (
                                <ListItem key={key} px={3} py={2} cursor="pointer" _hover={{ backgroundColor : "blue.500" }} onClick={() => handleSelectKey(key)}>
                                    {tag.name}
                                </ListItem>
                            ))}
                            {canCreate && (
                                <ListItem
                                    px={3} py={2}
                                    cursor="pointer"
                                    fontStyle="italic"
                                    _hover={{ backgroundColor: "red.100" }}
                                    onClick={handleCreateTag}
                                >
                                    {isCreating ? <Spinner size="xs" mr={2} /> : null}
                                    {`Créer le tag "${inputValue.trim()}"`}
                                </ListItem>
                            )}
                            {matchingEntries.length === 0 && !canCreate && (
                                <ListItem px={3} py={2}>
                                    <Text fontSize="sm" color="gray.500">Aucune catégorie</Text>
                                </ListItem>
                            )}
                        </List>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        </Box>
    );
}

// Fix PopoverTrigger using chakra 1.x and some version of @types/react
interface PopoverTriggerProps {
    children: ReactElement;
}

export const PopoverTrigger = ChakraPopoverTrigger as unknown as (props: PopoverTriggerProps) => JSX.Element;